import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Store,
  Package,
  Clock,
  CheckCircle,
  TrendingUp,
  DollarSign,
  Settings,
  BarChart3,
  Sparkles,
  ShoppingBag,
  ArrowRight,
  Loader2,
  MapPin,
  Tags,
  LogOut,
  Menu,
  X,
  Eye,
  ExternalLink,
  Receipt,
  UserCircle,
} from 'lucide-react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { useBusinessContext } from '../../hooks/useBusinessContext';
import { useOrders } from '../../hooks/useOrders';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { OrderTicketCard } from '../../components/business/OrderTicketCard';
import { HandoffScreen } from '../../components/business/HandoffScreen';
import { OrderDeliveryMapCard } from '../../components/shared/OrderDeliveryMapCard';
import { useOrderMapLocations } from '../../hooks/useOrderMapLocations';
import { ProductManager } from '../../components/business/ProductManager';
import { NotificationBell } from '../../components/business/NotificationBell';
import { cn, formatEtaClock, formatEtaRelative, formatBatchStop } from '../../lib/utils';
import type { BusinessDocument } from '../../types/business';

type TabType = 'orders' | 'products';

// Map order states to dashboard tabs.
// "Preparing" also covers DISPATCHING_DRIVER so an approved order stays visible
// to the store while the platform is finding a courier (otherwise it would
// briefly disappear from every tab).
function categorizeOrders(orders: any[]) {
  return {
    incoming: orders.filter(o => o.state === 'PENDING_BUSINESS_APPROVAL'),
    preparing: orders.filter(o => ['BUSINESS_APPROVED', 'DISPATCHING_DRIVER'].includes(o.state)),
    ready: orders.filter(o => o.state === 'DRIVER_ASSIGNED'),
  };
}

export function BusinessDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, signOut } = useAuth();
  const { businessId, isSeat, seatRole, can, loading: contextLoading } = useBusinessContext();
  const initialTab = (searchParams.get('tab') as TabType) || 'orders';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [orderSubTab, setOrderSubTab] = useState<'incoming' | 'preparing' | 'ready'>('incoming');
  const [selectedOrderForHandoff, setSelectedOrderForHandoff] = useState<string | null>(null);
  const [business, setBusiness] = useState<Partial<BusinessDocument> | null>(null);
  const [businessLoading, setBusinessLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (searchParams.has('tab')) {
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // A staff seat has no products tab, including via ?tab=products
  useEffect(() => {
    if (!contextLoading && activeTab === 'products' && !can('products')) {
      setActiveTab('orders');
    }
  }, [contextLoading, activeTab, can]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  // Fetch real business data — a seat resolves to its store, not its own uid
  useEffect(() => {
    if (contextLoading) return;
    if (!businessId) {
      setBusinessLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'businesses', businessId),
      (snap) => {
        if (snap.exists()) {
          setBusiness(snap.data() as Partial<BusinessDocument>);
        }
        setBusinessLoading(false);
      },
      (err) => {
        console.error('Error loading business:', err);
        setBusinessLoading(false);
      }
    );

    return () => unsubscribe();
  }, [businessId, contextLoading]);

  // Fetch real orders
  const { orders, loading: ordersLoading } = useOrders(businessId || '', 'business');
  const categorized = categorizeOrders(orders);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);

  // Business approve/reject handlers
  const handleAcceptOrder = async (orderId: string) => {
    setRespondingTo(orderId);
    try {
      const respond = httpsCallable(functions, 'businessRespondToOrder');
      await respond({ orderId, decision: 'approve' });
      console.log(`Order ${orderId} approved`);
    } catch (err: any) {
      console.error('Failed to approve order:', err);
      alert(`Failed to approve order: ${err?.message || 'Unknown error'}`);
    } finally {
      setRespondingTo(null);
    }
  };

  const handleDeclineOrder = async (orderId: string) => {
    const reason = window.prompt('Reason for declining (optional):') || 'Business declined';
    setRespondingTo(orderId);
    try {
      const respond = httpsCallable(functions, 'businessRespondToOrder');
      await respond({ orderId, decision: 'reject', reason });
      console.log(`Order ${orderId} declined`);
    } catch (err: any) {
      console.error('Failed to decline order:', err);
      alert(`Failed to decline order: ${err?.message || 'Unknown error'}`);
    } finally {
      setRespondingTo(null);
    }
  };

  // Compute real stats from orders
  const todaysOrders = orders.filter(o => {
    const created = o.createdAt instanceof Date ? o.createdAt : (o.createdAt as any)?.toDate?.();
    if (!created) return false;
    const today = new Date();
    return created.toDateString() === today.toDateString();
  });

  const todayRevenue = todaysOrders.reduce((sum, o) => sum + (o.total || 0), 0);

  // A seat sees the orders it personally handled, never store-wide revenue.
  const myOrdersToday = todaysOrders.filter(
    (o) => (o as any).acceptedBySeatUid === user?.uid
  );

  const stats = [
    {
      label: "Today's Orders",
      value: todaysOrders.length.toString(),
      icon: Package,
      empty: todaysOrders.length === 0,
    },
    isSeat
      ? {
          label: 'My Orders Today',
          value: myOrdersToday.length.toString(),
          icon: Receipt,
          empty: myOrdersToday.length === 0,
        }
      : {
          label: 'Revenue',
          value: todayRevenue > 0 ? `$${todayRevenue.toLocaleString()}` : '$0',
          icon: DollarSign,
          empty: todayRevenue === 0,
        },
    {
      label: 'Avg. Prep Time',
      value: business?.prepTimeEstimate ? `${business.prepTimeEstimate} min` : '—',
      icon: Clock,
      empty: false,
    },
    {
      label: 'Active Orders',
      value: orders.length.toString(),
      icon: TrendingUp,
      empty: orders.length === 0,
    },
  ];

  const canManageProducts = can('products');

  const orderSubTabs = [
    { id: 'incoming' as const, label: 'Incoming', count: categorized.incoming.length, urgent: true },
    { id: 'preparing' as const, label: 'Preparing', count: categorized.preparing.length },
    { id: 'ready' as const, label: 'Ready', count: categorized.ready.length },
  ];

  // Determine if business is open right now (simplified)
  const isOpen = business?.active ?? false;

  // Loading state
  if (businessLoading || contextLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto" />
          <p className="mt-4 text-body font-sans">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // If showing handoff screen for a ready order
  const handoffOrder = categorized.ready.find(o => o.id === selectedOrderForHandoff);
  if (handoffOrder && selectedOrderForHandoff) {
    return (
      <HandoffView
        order={handoffOrder}
        onClose={() => setSelectedOrderForHandoff(null)}
        isSeat={isSeat}
      />
    );
  }

  const hasAnyOrders = orders.length > 0;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {business?.logo ? (
                <img
                  src={business.logo}
                  alt={business.name || 'Boutique'}
                  className="w-10 h-10 rounded-xl object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                  <Store className="h-5 w-5 text-white" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-serif text-heading">
                    {business?.name || 'Your Boutique'}
                  </h1>
                  {isSeat && (
                    <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold uppercase tracking-wide">
                      {seatRole === 'manager' ? 'Manager' : 'Staff'}
                    </span>
                  )}
                </div>
                <p className={cn(
                  "text-xs flex items-center",
                  isOpen ? "text-success" : "text-body"
                )}>
                  <span className={cn(
                    "w-2 h-2 rounded-full mr-1.5",
                    isOpen ? "bg-success" : "bg-gray-300"
                  )} />
                  {isOpen ? 'Open • Accepting Orders' : 'Closed'}
                </p>
              </div>
            </div>

            {/* Desktop actions */}
            <div className="hidden sm:flex items-center space-x-2">
              {isSeat ? (
                <Button variant="outline" size="sm" onClick={() => navigate('/business/my-sales')}>
                  <Receipt className="h-4 w-4 mr-1.5" />
                  My Sales
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => navigate('/business/analytics')}>
                  <BarChart3 className="h-4 w-4 mr-1.5" />
                  Analytics
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => navigate('/business/hours')}>
                <Clock className="h-4 w-4 mr-1.5" />
                Hours
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`/boutique/${businessId}`, '_blank')}
              >
                <Eye className="h-4 w-4 mr-1.5" />
                View Store
              </Button>
              <NotificationBell />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(isSeat ? '/business/profile' : '/business/settings')}
              >
                {isSeat ? <UserCircle className="h-5 w-5" /> : <Settings className="h-5 w-5" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>

            {/* Mobile actions */}
            <div className="flex sm:hidden items-center space-x-1">
              <NotificationBell />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile dropdown menu */}
          {mobileMenuOpen && (
            <div className="sm:hidden mt-3 pt-3 border-t border-gray-100 space-y-1 animate-in slide-in-from-top-2 duration-200">
              <button
                onClick={() => {
                  navigate(isSeat ? '/business/my-sales' : '/business/analytics');
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium text-heading hover:bg-gray-50 transition-colors"
              >
                {isSeat ? (
                  <Receipt className="h-4 w-4 text-primary" />
                ) : (
                  <BarChart3 className="h-4 w-4 text-primary" />
                )}
                <span>{isSeat ? 'My Sales' : 'Analytics'}</span>
              </button>
              <button
                onClick={() => {
                  navigate('/business/hours');
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium text-heading hover:bg-gray-50 transition-colors"
              >
                <Clock className="h-4 w-4 text-primary" />
                <span>Store Hours</span>
              </button>
              <button
                onClick={() => {
                  window.open(`/boutique/${businessId}`, '_blank');
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium text-heading hover:bg-gray-50 transition-colors"
              >
                <Eye className="h-4 w-4 text-primary" />
                <span>View My Store</span>
              </button>
              <button
                onClick={() => {
                  navigate(isSeat ? '/business/profile' : '/business/settings');
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium text-heading hover:bg-gray-50 transition-colors"
              >
                {isSeat ? (
                  <UserCircle className="h-4 w-4 text-gray-500" />
                ) : (
                  <Settings className="h-4 w-4 text-gray-500" />
                )}
                <span>{isSeat ? 'My Profile' : 'Settings'}</span>
              </button>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {business?.featured && (
          <button
            type="button"
            onClick={() => navigate('/business/settings?section=identity')}
            className="w-full mb-6 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4 flex items-center gap-4 hover:border-amber-300 hover:shadow-sm transition-all text-left"
          >
            <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-heading">You're featured on the FASH homepage</p>
              <p className="text-xs text-body mt-0.5">
                Upload or update the hero image shoppers see when your store rotates in the featured carousel.
              </p>
            </div>
            <ArrowRight className="h-4 w-4 text-amber-500 flex-shrink-0" />
          </button>
        )}

        {/* Welcome Banner for new businesses with no orders */}
        {!hasAnyOrders && activeTab === 'orders' && canManageProducts && (
          <WelcomeBanner
            businessName={business?.name || 'there'}
            address={business?.formattedAddress}
            onAddProducts={() => setActiveTab('products')}
          />
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {stats.map((stat) => (
            <Card key={stat.label}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <stat.icon className="h-5 w-5 text-primary" />
                </div>
                <p className={cn(
                  "text-2xl font-bold mt-2",
                  stat.empty ? "text-gray-300" : "text-heading"
                )}>
                  {stat.value}
                </p>
                <p className="text-xs text-body">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Tabs: Orders | Products */}
        <div className="flex space-x-1 bg-white rounded-xl p-1 mb-6">
          <button
            onClick={() => setActiveTab('orders')}
            className={cn(
              "flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center space-x-2",
              activeTab === 'orders'
                ? "bg-primary text-white shadow-sm"
                : "text-body hover:bg-gray-50"
            )}
          >
            <Package className="h-4 w-4" />
            <span>Orders</span>
            {orders.length > 0 && (
              <span className={cn(
                "px-2 py-0.5 rounded-full text-xs font-bold",
                activeTab === 'orders'
                  ? "bg-white/20 text-white"
                  : "bg-gray-100 text-gray-600"
              )}>
                {orders.length}
              </span>
            )}
          </button>
          {canManageProducts && (
            <button
              onClick={() => setActiveTab('products')}
              className={cn(
                "flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center space-x-2",
                activeTab === 'products'
                  ? "bg-primary text-white shadow-sm"
                  : "text-body hover:bg-gray-50"
              )}
            >
              <Tags className="h-4 w-4" />
              <span>Products</span>
            </button>
          )}
        </div>

        {/* ── ORDERS TAB ─── */}
        {activeTab === 'orders' && (
          <>
            {/* Order Sub-tabs */}
            <div className="flex space-x-1 bg-gray-50 rounded-xl p-1 mb-4 overflow-x-auto">
              {orderSubTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setOrderSubTab(tab.id)}
                  className={cn(
                    "flex-1 min-w-[100px] px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center space-x-2",
                    orderSubTab === tab.id
                      ? "bg-white text-heading shadow-sm"
                      : "text-body hover:bg-gray-100"
                  )}
                >
                  <span>{tab.label}</span>
                  {tab.count > 0 && (
                    <span className={cn(
                      "px-2 py-0.5 rounded-full text-xs font-bold",
                      orderSubTab === tab.id
                        ? "bg-primary text-white"
                        : tab.urgent && tab.count > 0
                          ? "bg-error text-white animate-pulse"
                          : "bg-gray-200 text-gray-600"
                    )}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Loading orders */}
            {ordersLoading && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto mb-4" />
                  <p className="text-sm text-body">Loading orders...</p>
                </CardContent>
              </Card>
            )}

            {/* Orders Grid */}
            {!ordersLoading && (
              <div className="space-y-4">
                {orderSubTab === 'incoming' && (
                  <>
                    {categorized.incoming.length === 0 ? (
                      <EmptyState
                        icon={Package}
                        title="No incoming orders"
                        description={hasAnyOrders
                          ? "New orders will appear here when shoppers place them"
                          : "Once shoppers discover your boutique and place orders, they'll appear right here"
                        }
                      />
                    ) : (
                      categorized.incoming.map((order) => (
                        <OrderTicketCard
                          key={order.id}
                          order={{
                            id: order.id,
                            items: order.items || [],
                            total: order.total || 0,
                            createdAt: order.createdAt instanceof Date
                              ? order.createdAt
                              : (order.createdAt as any)?.toDate?.() || new Date(),
                            expiresAt: (order as any).approval?.expiresAt instanceof Date
                              ? (order as any).approval.expiresAt
                              : (order as any).approval?.expiresAt?.toDate?.() || new Date(Date.now() + 30 * 60 * 1000),
                          }}
                          onAccept={() => handleAcceptOrder(order.id)}
                          onDecline={() => handleDeclineOrder(order.id)}
                        />
                      ))
                    )}
                  </>
                )}

                {orderSubTab === 'preparing' && (
                  <>
                    {categorized.preparing.length === 0 ? (
                      <EmptyState
                        icon={Clock}
                        title="Nothing in preparation"
                        description="Accepted orders will move here while you prepare them"
                      />
                    ) : (
                      categorized.preparing.map((order) => (
                        <PreparingCard key={order.id} order={order} />
                      ))
                    )}
                  </>
                )}

                {orderSubTab === 'ready' && (
                  <>
                    {categorized.ready.length === 0 ? (
                      <EmptyState
                        icon={CheckCircle}
                        title="No orders ready"
                        description="Orders awaiting courier pickup will appear here"
                      />
                    ) : (
                      categorized.ready.map((order) => (
                        <ReadyCard
                          key={order.id}
                          order={order}
                          onViewHandoff={() => setSelectedOrderForHandoff(order.id)}
                        />
                      ))
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* ── PRODUCTS TAB ─── */}
        {activeTab === 'products' && canManageProducts && businessId && (
          <ProductManager
            businessId={businessId}
            businessName={business?.name || 'My Boutique'}
            allowImport={!isSeat}
          />
        )}
      </main>
    </div>
  );
}

// ── Welcome Banner for new businesses ──────────────────────────────
function WelcomeBanner({ businessName, address, onAddProducts }: { 
  businessName: string; 
  address?: string;
  onAddProducts: () => void;
}) {
  return (
    <Card className="mb-6 border-0 bg-gradient-to-br from-primary/5 via-purple-50 to-white overflow-hidden">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-serif text-heading mb-1">
              Welcome to Fash, {businessName}!
            </h2>
            <p className="text-sm text-body mb-4">
              Your boutique is live and ready to receive orders. Here are a few things to get started:
            </p>

            {address && (
              <div className="flex items-center text-xs text-body mb-4">
                <MapPin className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                <span className="truncate">{address}</span>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3">
              <GettingStartedItem
                icon={ShoppingBag}
                title="Add Products"
                description="Upload your inventory so shoppers can discover your items"
                onClick={onAddProducts}
                highlight
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GettingStartedItem({
  icon: Icon,
  title,
  description,
  onClick,
  highlight,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  onClick?: () => void;
  highlight?: boolean;
}) {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper 
      onClick={onClick}
      className={cn(
        "bg-white rounded-xl p-3 border text-left transition-all",
        highlight
          ? "border-primary/30 hover:border-primary hover:shadow-md cursor-pointer ring-1 ring-primary/10"
          : "border-gray-100",
        onClick && "cursor-pointer hover:bg-gray-50"
      )}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium text-heading">{title}</span>
        {highlight && (
          <ArrowRight className="h-3.5 w-3.5 text-primary ml-auto" />
        )}
      </div>
      <p className="text-xs text-body leading-relaxed">{description}</p>
    </Wrapper>
  );
}

// ── Empty State ────────────────────────────────────────────────────
function EmptyState({ icon: Icon, title, description }: {
  icon: React.ElementType;
  title: string;
  description: string;
}) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="font-medium text-heading">{title}</h3>
        <p className="text-sm text-body mt-1">{description}</p>
      </CardContent>
    </Card>
  );
}

// ── Preparing Card ─────────────────────────────────────────────────
function PreparingCard({ order }: { order: any }) {
  const isDispatching = order.state === 'DISPATCHING_DRIVER';
  const pickupClock = formatEtaClock(order.uber?.pickupEta);
  const pickupRel = formatEtaRelative(order.uber?.pickupEta);
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-medium text-heading">
              Order #{order.id.slice(-6)}
            </p>
            <p className="text-sm text-body">{order.customerName || 'Customer'}</p>
          </div>
          {isDispatching ? (
            <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              Finding a driver
            </span>
          ) : (
            <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
              Preparing
            </span>
          )}
        </div>
        <div className="space-y-2">
          {(order.items || []).map((item: any, idx: number) => (
            <div key={idx} className="flex items-center justify-between text-sm">
              <span className="text-body">
                {item.name}
                {item.size ? ` (${item.size})` : ''}
              </span>
              <span className="text-heading">&times;{item.quantity || 1}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100">
          {isDispatching ? (
            pickupClock ? (
              <div className="flex items-center justify-center gap-2 text-sm text-primary py-2 font-medium">
                <Clock className="h-4 w-4" />
                Courier arriving {pickupRel ? `${pickupRel} ` : ''}(~{pickupClock})
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 text-sm text-blue-600 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching for a courier&hellip; you&rsquo;ll be notified when one is assigned.
              </div>
            )
          ) : (
            <Button fullWidth>
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark Ready for Pickup
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Ready Card ─────────────────────────────────────────────────────
/**
 * Handoff screen wrapper: subscribes to the order's private pickup-code
 * secret (orders/{id}/secrets/pickup — readable only by this business and
 * admins) and shows the live verification state.
 */
function HandoffView({ order, onClose, isSeat }: { order: any; onClose: () => void; isSeat: boolean }) {
  const [pickupCode, setPickupCode] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [liveOrder, setLiveOrder] = useState(order);

  useEffect(() => {
    setLiveOrder(order);
  }, [order]);

  // Whoever opens the handoff is the person putting the bag in the courier's
  // hands, which is what the seat's own sales view reports on.
  useEffect(() => {
    if (!isSeat) return;
    const record = httpsCallable(functions, 'recordSeatHandoff');
    record({ orderId: order.id }).catch((err) =>
      console.warn('[Handoff] Could not record seat handoff:', err?.message)
    );
  }, [isSeat, order.id]);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'orders', order.id),
      (snap) => {
        if (snap.exists()) {
          setLiveOrder({ id: snap.id, ...snap.data() });
        }
      },
      () => {},
    );
    return unsub;
  }, [order.id]);

  const mapLocations = useOrderMapLocations(liveOrder);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'orders', order.id, 'secrets', 'pickup'),
      (snap) => {
        if (snap.exists()) {
          setPickupCode(snap.data().code || null);
          setVerified(snap.data().verified === true);
        } else {
          setCodeError('Pickup code not generated yet');
        }
      },
      () => setCodeError('Unable to load pickup code')
    );
    return unsub;
  }, [order.id]);

  // Internal driver takes precedence; Uber courier as fallback
  const driverName = liveOrder.driver?.name || liveOrder.uber?.courier?.name || 'Courier';
  const driverPhotoUrl = liveOrder.driver?.photoUrl || liveOrder.uber?.courier?.imgUrl;
  const trackingUrl = liveOrder.trackingUrl || liveOrder.uber?.trackingUrl;

  // Before pickup, the store cares about when the courier reaches THEM
  // (pickup_eta). After pickup, show the customer dropoff ETA.
  const pretPickup = ['DISPATCHING_DRIVER', 'DRIVER_ASSIGNED'].includes(liveOrder.state);
  const pickupClock = formatEtaClock(liveOrder.uber?.pickupEta);
  const pickupRel = formatEtaRelative(liveOrder.uber?.pickupEta);
  const dropoffClock = formatEtaClock(liveOrder.uber?.dropoffEta);

  const baseMapLabel =
    liveOrder.state === 'DISPATCHING_DRIVER'
      ? 'Courier on the way to your store'
      : liveOrder.state === 'DRIVER_ASSIGNED'
        ? 'Courier arriving at pickup'
        : liveOrder.state === 'PICKED_UP'
          ? 'Order picked up'
          : liveOrder.state === 'OUT_FOR_DELIVERY'
            ? 'Out for delivery'
            : 'Live delivery map';
  const mapEtaClock = pretPickup ? pickupClock : dropoffClock;
  const mapStatusLabel = mapEtaClock ? `${baseMapLabel} · ${mapEtaClock}` : baseMapLabel;
  const batchStop = formatBatchStop(
    liveOrder.uber?.batchId,
    liveOrder.uber?.dropoffSequence,
    liveOrder.uber?.totalDropoffCount,
  );

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-lg mx-auto space-y-4">
        <Button variant="ghost" onClick={onClose} className="text-body">
          <X className="h-4 w-4 mr-2" />
          Back to dashboard
        </Button>

        {verified && (
          <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-success/10 text-success font-medium">
            <CheckCircle className="h-5 w-5" />
            Code verified — package picked up
          </div>
        )}

        {!verified && pretPickup && (pickupRel || pickupClock) && (
          <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-primary/10 text-primary font-medium">
            <Clock className="h-5 w-5" />
            {pickupRel && pickupClock
              ? `Courier arriving ${pickupRel} (~${pickupClock})`
              : `Courier arriving ~${pickupClock}`}
          </div>
        )}

        {!verified && batchStop && (
          <div className="flex items-center justify-center gap-2 p-2.5 rounded-xl bg-amber-50 text-amber-700 text-sm font-medium">
            <Package className="h-4 w-4" />
            {batchStop} on this courier&rsquo;s route — they may make other stops
          </div>
        )}

        {codeError ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Loader2 className="h-6 w-6 text-primary animate-spin mx-auto mb-3" />
              <p className="text-body text-sm">{codeError}</p>
            </CardContent>
          </Card>
        ) : (
          <HandoffScreen
            orderId={liveOrder.id}
            driverName={driverName}
            driverPhotoUrl={driverPhotoUrl}
            pickupCode={pickupCode || '····'}
          />
        )}

        <OrderDeliveryMapCard
          store={mapLocations.store}
          dropoff={mapLocations.dropoff}
          courier={mapLocations.courier}
          isLoaded={mapLocations.isLoaded}
          geocoding={mapLocations.geocoding}
          hasAnyPoint={mapLocations.hasAnyPoint}
          statusLabel={mapStatusLabel}
          legendLabels={{ dropoff: 'Customer' }}
          mapClassName="h-56"
        />

        {trackingUrl && (
          <Button variant="outline" fullWidth asChild>
            <a href={trackingUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              Track on Uber
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}

function ReadyCard({ order, onViewHandoff }: { order: any; onViewHandoff: () => void }) {
  const courier = order.uber?.courier || order.driver;
  const pickupClock = formatEtaClock(order.uber?.pickupEta);
  const pickupRel = formatEtaRelative(order.uber?.pickupEta);
  const courierArrivingLabel = pickupClock
    ? `Courier arriving ${pickupRel ? `${pickupRel} ` : ''}(~${pickupClock})`
    : 'Courier arriving';

  return (
    <Card className="border-l-4 border-l-success">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-medium text-heading">
              Order #{order.id.slice(-6)}
            </p>
            <p className="text-sm text-body">{order.customerName || 'Customer'}</p>
          </div>
          <span className="px-3 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
            Ready
          </span>
        </div>

        {courier && (
          <div className="bg-gray-50 rounded-xl p-3 mb-3">
            <p className="text-xs text-body mb-2">{courierArrivingLabel}</p>
            <div className="flex items-center space-x-3">
              {courier.imgUrl ? (
                <img
                  src={courier.imgUrl}
                  alt={courier.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <Store className="h-5 w-5 text-gray-400" />
                </div>
              )}
              <div>
                <p className="font-medium text-heading text-sm">{courier.name}</p>
                {courier.vehicleType && (
                  <p className="text-xs text-body">{courier.vehicleType}</p>
                )}
              </div>
            </div>
          </div>
        )}

        <Button fullWidth onClick={onViewHandoff}>
          View Handoff Screen
        </Button>
      </CardContent>
    </Card>
  );
}
